import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { PRODUCT_STATUS_CONFIG, normalizeProductStatus } from './constants.js';
import { money, signedMoney } from './drawerFormat.js';
import {
  BILLING_EVIDENCE_DRAG_END,
  BILLING_EVIDENCE_DRAG_START,
  EVIDENCE_DRAG_TYPE,
  hasBillingEvidenceDragType,
} from './billingDragTypes.js';

export const PANEL_HEADER_WITH_TABS_MIN_HEIGHT = 73;

export const TotalDeltaPopover = ({ rows = [] }) => {
  const [anchor, setAnchor] = useState(null);
  if (!rows.length) return null;
  return (
    <>
      <Tooltip title="Why this total changed">
        <IconButton
          size="small"
          aria-label="Why total changed"
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={{ width: 24, height: 24 }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Stack spacing={0.75} sx={{ p: 1.25, minWidth: 260, maxWidth: 360 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            Total change drivers
          </Typography>
          {rows.map((row) => (
            <Stack key={row.id || `${row.productCode}-${row.delta}`} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{row.productCode}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>{row.label} - {row.status}</Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                {signedMoney(row.delta)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Popover>
    </>
  );
};

const RemoveDropLane = ({ dragContext, removeTarget, onEvidenceRemoveDrop }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const canRemove = Boolean(
    dragContext
    && removeTarget
    && onEvidenceRemoveDrop
    && dragContext.source === removeTarget.source,
  );
  const handleDrop = (event) => {
    if (!canRemove) return;
    const rawPayload = event.dataTransfer.getData(EVIDENCE_DRAG_TYPE);
    if (!rawPayload) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    try {
      onEvidenceRemoveDrop(JSON.parse(rawPayload), removeTarget);
      window.dispatchEvent(new CustomEvent(BILLING_EVIDENCE_DRAG_END));
    } catch {
      // Ignore unrelated drops.
    }
  };

  return (
    <Collapse in={canRemove} unmountOnExit>
      <Box
        data-billing-remove-drop-zone="true"
        onDragOver={(event) => {
          if (!canRemove || !hasBillingEvidenceDragType(event.dataTransfer)) return;
          event.preventDefault();
          event.stopPropagation();
          event.dataTransfer.dropEffect = 'copy';
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        sx={{
          mt: 0.5,
          px: 1.25,
          py: 2,
          minHeight: 72,
          border: '1px dashed',
          borderColor: isDragOver ? 'error.main' : 'error.light',
          borderRadius: 1,
          bgcolor: (theme) => (isDragOver ? `${theme.palette.error.main}1A` : theme.palette.action.hover),
          color: isDragOver ? 'error.dark' : 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          transition: 'background-color 100ms, border-color 100ms, color 100ms',
        }}
      >
        <DeleteOutlineIcon fontSize="small" />
        <Typography variant="body2" sx={{ fontWeight: 900 }}>
          Remove from {removeTarget.sourceLabel || removeTarget.source}
        </Typography>
      </Box>
    </Collapse>
  );
};

export const ColumnShell = ({ label, total, originalTotal = null, totalStatus = null, totalDeltaRows = [], accent, dropTarget = null, removeTarget = null, onEvidenceDrop = null, onEvidenceRemoveDrop = null, children }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragContext, setDragContext] = useState(null);
  const canDrop = Boolean(dropTarget && onEvidenceDrop);
  const showTotalStatus = Boolean(totalStatus && originalTotal !== null);
  useEffect(() => {
    const handleDragStart = (event) => setDragContext(event.detail || null);
    const handleDragEnd = () => {
      setDragContext(null);
      setIsDragOver(false);
    };
    window.addEventListener(BILLING_EVIDENCE_DRAG_START, handleDragStart);
    window.addEventListener(BILLING_EVIDENCE_DRAG_END, handleDragEnd);
    return () => {
      window.removeEventListener(BILLING_EVIDENCE_DRAG_START, handleDragStart);
      window.removeEventListener(BILLING_EVIDENCE_DRAG_END, handleDragEnd);
    };
  }, []);
  const handleDrop = (event) => {
    if (!canDrop) return;
    const rawPayload = event.dataTransfer.getData(EVIDENCE_DRAG_TYPE);
    if (!rawPayload) return;
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    try {
      onEvidenceDrop(JSON.parse(rawPayload), dropTarget);
      window.dispatchEvent(new CustomEvent(BILLING_EVIDENCE_DRAG_END));
    } catch {
      // Ignore unrelated drops.
    }
  };
  return (
    <Paper
      variant="outlined"
      onDragOver={(event) => {
        if (!canDrop || !hasBillingEvidenceDragType(event.dataTransfer)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
      sx={{
        p: 2,
        flex: 1,
        minWidth: 0,
        borderRadius: 1.5,
        position: 'relative',
        borderTop: '3px solid',
        borderTopColor: accent,
        outline: isDragOver ? '2px solid' : 'none',
        outlineColor: 'info.main',
        outlineOffset: 2,
      }}
    >
      <Stack spacing={1.5}>
      <Box
        sx={{
          minHeight: showTotalStatus ? 58 : 42,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          pb: 0.5,
        }}
        data-billing-column-header="true"
      >
        <Typography
          variant="overline"
          sx={{
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: 0.6,
            fontSize: 10.5,
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.4,
          }}
        >
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: accent,
              letterSpacing: -0.5,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.15,
              mt: 0.25,
            }}
          >
            {money(total)}
          </Typography>
          <TotalDeltaPopover rows={totalDeltaRows} />
        </Stack>
        {showTotalStatus && (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textDecoration: 'line-through',
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.2,
              }}
            >
              {money(originalTotal)}
            </Typography>
            <Chip
              size="small"
              label={totalStatus === 'saved' ? 'saved change' : 'draft change'}
              color={totalStatus === 'saved' ? 'success' : 'warning'}
              variant="outlined"
              sx={{ height: 18, '& .MuiChip-label': { px: 0.65, fontSize: 10, fontWeight: 800 } }}
            />
          </Stack>
        )}
      </Box>
      <Divider />
      {children}
      <RemoveDropLane
        dragContext={dragContext}
        removeTarget={removeTarget}
        onEvidenceRemoveDrop={onEvidenceRemoveDrop}
      />
      </Stack>
    </Paper>
  );
};

export const Section = ({ title, count, countColor, defaultOpen = true, emptyText, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasChildren = React.Children.count(children) > 0;
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        sx={{
          cursor: 'pointer',
          py: 0.5,
          px: 0.5,
          mx: -0.5,
          borderRadius: 0.75,
          userSelect: 'none',
          transition: 'background-color 100ms, color 100ms',
          '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 1,
          },
        }}
      >
        <KeyboardArrowDownIcon
          fontSize="small"
          sx={{
            color: 'text.secondary',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 160ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontSize: 11,
            color: 'text.secondary',
            flex: 1,
          }}
        >
          {title}
        </Typography>
        {typeof count === 'number' && (
          <Chip
            size="small"
            label={count}
            color={countColor || 'default'}
            variant="outlined"
            sx={{ height: 18, fontSize: 11, fontWeight: 600, '& .MuiChip-label': { px: 0.75 } }}
          />
        )}
      </Stack>
      <Collapse in={open} timeout={160}>
        <Stack spacing={0.75} sx={{ pl: 2.5, pr: 0.25, pt: 0.5 }}>
          {hasChildren ? children : (
            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
              {emptyText || 'No rows'}
            </Typography>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

export const StatusChip = ({ status, size = 'small' }) => {
  const normalizedStatus = status ? normalizeProductStatus(status, '') : '';
  const cfg = normalizedStatus ? PRODUCT_STATUS_CONFIG[normalizedStatus] : null;
  if (!cfg) {
    return (
      <Chip
        size={size}
        label={status || '--'}
        variant="outlined"
        sx={{ height: size === 'small' ? 20 : 24, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
      />
    );
  }
  return (
    <Chip
      size={size}
      label={cfg.label}
      sx={{
        height: size === 'small' ? 20 : 24,
        '& .MuiChip-label': { px: 0.75, fontSize: 11 },
        ...cfg.chipSx,
      }}
    />
  );
};
